import { useState } from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight, BookOpen, FileText, Video, Download, Search, Calendar } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { Input } from '../components/ui/input';

const categories = ['All', 'Articles', 'Whitepapers', 'Webinars', 'Case Studies'];

const resources = [
  {
    id: 1,
    type: 'Article',
    title: '2024 Healthcare Revenue Cycle Trends',
    excerpt: 'Discover the key trends shaping revenue cycle management in the coming year and how to prepare your practice.',
    date: 'Dec 15, 2024',
    readTime: '8 min read',
    icon: BookOpen,
    featured: true,
  },
  {
    id: 2,
    type: 'Whitepaper',
    title: 'The Complete Guide to Denial Management',
    excerpt: 'A comprehensive guide to reducing claim denials and improving your first-pass acceptance rate.',
    date: 'Dec 10, 2024',
    readTime: '15 min read',
    icon: FileText,
  },
  {
    id: 3,
    type: 'Webinar',
    title: 'Optimizing Patient Collections in 2024',
    excerpt: 'Join our experts for strategies to improve patient payment rates while maintaining satisfaction.',
    date: 'Dec 5, 2024',
    readTime: '45 min',
    icon: Video,
  },
  {
    id: 4,
    type: 'Case Study',
    title: 'How Valley Medical Increased Revenue by 35%',
    excerpt: 'Learn how a multi-specialty practice transformed their revenue cycle with our solutions.',
    date: 'Nov 28, 2024',
    readTime: '10 min read',
    icon: FileText,
  },
  {
    id: 5,
    type: 'Article',
    title: 'HIPAA Compliance Best Practices',
    excerpt: 'Essential guidelines for maintaining HIPAA compliance in your healthcare organization.',
    date: 'Nov 20, 2024',
    readTime: '6 min read',
    icon: BookOpen,
  },
  {
    id: 6,
    type: 'Whitepaper',
    title: 'The ROI of Outsourced Revenue Cycle Management',
    excerpt: 'Detailed analysis of the financial benefits of partnering with an MSO for RCM services.',
    date: 'Nov 15, 2024',
    readTime: '20 min read',
    icon: FileText,
  },
];

export const Resources = () => {
  const [activeCategory, setActiveCategory] = useState('All');
  const [searchQuery, setSearchQuery] = useState('');

  const filteredResources = resources.filter((resource) => {
    const matchesCategory = activeCategory === 'All' || resource.type === activeCategory.slice(0, -1) || resource.type + 's' === activeCategory;
    const matchesSearch = resource.title.toLowerCase().includes(searchQuery.toLowerCase()) ||
                          resource.excerpt.toLowerCase().includes(searchQuery.toLowerCase());
    return matchesCategory && matchesSearch;
  });

  const featuredResource = resources.find((r) => r.featured);

  return (
    <div data-testid="resources-page">
      {/* Hero Section */}
      <section className="relative py-24 md:py-32 bg-navy" data-testid="resources-hero">
        <div className="container-custom">
          <div className="max-w-3xl">
            <span className="inline-block px-4 py-2 bg-emerald text-white font-semibold text-xs uppercase tracking-wider mb-6">
              Resources
            </span>
            <h1 className="font-manrope font-bold text-4xl sm:text-5xl text-white leading-tight mb-6">
              Insights & Knowledge Center
            </h1>
            <p className="text-lg text-navy-200 leading-relaxed">
              Stay informed with the latest healthcare management insights, best practices, and industry trends.
            </p>
          </div>
        </div>
      </section>

      {/* Featured Resource */}
      {featuredResource && (
        <section className="py-12 bg-white" data-testid="featured-resource">
          <div className="container-custom">
            <Card className="bg-emerald-50 border-none shadow-card -mt-20 relative z-10">
              <CardContent className="p-8 md:p-12">
                <div className="grid md:grid-cols-2 gap-8 items-center">
                  <div>
                    <span className="inline-block px-3 py-1 bg-emerald text-white text-xs font-semibold uppercase tracking-wider mb-4">
                      Featured
                    </span>
                    <h2 className="font-manrope font-bold text-2xl md:text-3xl text-navy mb-4">
                      {featuredResource.title}
                    </h2>
                    <p className="text-navy-600 mb-6">{featuredResource.excerpt}</p>
                    <div className="flex items-center gap-4 text-sm text-navy-500 mb-6">
                      <span className="flex items-center gap-1">
                        <Calendar className="w-4 h-4" />
                        {featuredResource.date}
                      </span>
                      <span>{featuredResource.readTime}</span>
                    </div>
                    <Button 
                      className="bg-navy hover:bg-navy-700 text-white rounded-none font-semibold tracking-wide uppercase text-xs h-12 px-8"
                      data-testid="featured-resource-cta"
                    >
                      Read Article
                      <ArrowRight className="ml-2 w-4 h-4" />
                    </Button>
                  </div>
                  <div className="hidden md:block">
                    <div className="bg-navy rounded-sm p-8 text-center">
                      <BookOpen className="w-24 h-24 text-emerald mx-auto mb-4" />
                      <p className="text-white font-manrope font-semibold">Latest Industry Insights</p>
                    </div>
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>
        </section>
      )}

      {/* Search and Filter */}
      <section className="py-12 bg-navy-50" data-testid="resources-filter">
        <div className="container-custom">
          <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-6">
            {/* Categories */}
            <div className="flex flex-wrap gap-2">
              {categories.map((category) => (
                <button
                  key={category}
                  onClick={() => setActiveCategory(category)}
                  className={`px-4 py-2 text-sm font-medium transition-colors ${
                    activeCategory === category
                      ? 'bg-navy text-white'
                      : 'bg-white text-navy-600 hover:bg-navy-100'
                  }`}
                  data-testid={`category-${category.toLowerCase()}`}
                >
                  {category}
                </button>
              ))}
            </div>

            {/* Search */}
            <div className="relative w-full md:w-64">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-navy-400" />
              <Input
                type="text"
                placeholder="Search resources..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="pl-10 rounded-none border-silver h-11 bg-white"
                data-testid="resources-search-input"
              />
            </div>
          </div>
        </div>
      </section>

      {/* Resources Grid */}
      <section className="section-padding bg-navy-50" data-testid="resources-grid">
        <div className="container-custom">
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
            {filteredResources.filter(r => !r.featured).map((resource) => (
              <Card 
                key={resource.id}
                className="bg-white border-silver-light shadow-card card-hover group"
                data-testid={`resource-${resource.id}`}
              >
                <CardContent className="p-6">
                  <div className="flex items-center gap-3 mb-4">
                    <div className="w-10 h-10 rounded-sm bg-navy-50 group-hover:bg-emerald flex items-center justify-center transition-colors">
                      <resource.icon className="w-5 h-5 text-navy group-hover:text-white transition-colors" />
                    </div>
                    <span className="text-xs font-semibold text-emerald uppercase tracking-wider">{resource.type}</span>
                  </div>
                  <h3 className="font-manrope font-semibold text-navy text-lg mb-2 group-hover:text-emerald transition-colors">
                    {resource.title}
                  </h3>
                  <p className="text-sm text-navy-600 mb-4 line-clamp-2">{resource.excerpt}</p>
                  <div className="flex items-center justify-between text-xs text-navy-500">
                    <span>{resource.date}</span>
                    <span>{resource.readTime}</span>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>

          {filteredResources.length === 0 && (
            <div className="text-center py-12">
              <p className="text-navy-600">No resources found matching your criteria.</p>
            </div>
          )}
        </div>
      </section>

      {/* Newsletter CTA */}
      <section className="py-20 bg-navy" data-testid="newsletter-section">
        <div className="container-custom">
          <div className="max-w-2xl mx-auto text-center">
            <h2 className="font-manrope font-bold text-3xl text-white mb-4">
              Stay Informed
            </h2>
            <p className="text-navy-200 mb-8">
              Subscribe to our newsletter for the latest healthcare management insights delivered to your inbox.
            </p>
            <form className="flex flex-col sm:flex-row gap-4 max-w-md mx-auto">
              <Input
                type="email"
                placeholder="Enter your email"
                className="flex-1 rounded-none border-navy-600 h-12 bg-navy-700 text-white placeholder:text-navy-300"
                data-testid="newsletter-email-input"
              />
              <Button 
                type="submit"
                className="bg-emerald hover:bg-emerald-600 text-white rounded-none font-semibold tracking-wide uppercase text-xs h-12 px-8"
                data-testid="newsletter-submit-btn"
              >
                Subscribe
              </Button>
            </form>
          </div>
        </div>
      </section>

      {/* Download CTA */}
      <section className="section-padding bg-emerald" data-testid="download-cta">
        <div className="container-custom text-center">
          <Download className="w-16 h-16 text-white mx-auto mb-6 opacity-80" />
          <h2 className="font-manrope font-bold text-3xl text-white mb-4">
            Free Revenue Cycle Assessment Checklist
          </h2>
          <p className="text-emerald-100 text-lg mb-8 max-w-2xl mx-auto">
            Download our comprehensive checklist to evaluate your current revenue cycle performance.
          </p>
          <Button 
            className="bg-white text-emerald hover:bg-navy hover:text-white rounded-none font-semibold tracking-wide uppercase text-xs h-12 px-8"
            data-testid="download-checklist-btn"
          >
            Download Free Checklist
            <Download className="ml-2 w-4 h-4" />
          </Button>
        </div>
      </section>
    </div>
  );
};
